import { InputField, SelectField, STYLE_STATIC } from '@eb/eds-input-field';
import { Radio } from '@eb/eds-radio';
import React from 'react';
import {
    EVENT_PRIVACY_ACCESS_LINK,
    EVENT_PRIVACY_ACCESS_PASSWORD,
    EVENT_PRIVACY_ACCESS_VALUES,
    LISTED_PRIVATE,
    LISTED_PUBLIC,
    LOCALIZED_TEXT,
    PASSWORD_LABEL,
    PASSWORD_MAX_LENGTH,
    PRIVACY_VALUES,
} from '../constants';
import './PrivacySettings.scss';

interface PrivacySettingsProps {
    eventPassword?: string;
    isDisabled?: boolean;
    isButtonDisabled?: boolean;
    isButtonShown?: boolean;
    onButtonClick?: () => void;
    onEventPasswordChange?: () => void;
    onPrivacyAccessChange?: () => void;
    onPrivacyListingChange?: () => void;
    privacyAccess?: string;
    privacyListing?: string;
    privacyValues?: PrivacyValue[];
}

interface PrivacyValue {
    value: string;
    display: JSX.Element;
}

const RadioItemWithSecondaryText = (props: {
    display: string;
    secondaryText: string;
}) => (
    <div>
        <span className="eds-text-color--grey-800">{props.display}</span>
        <span className="eds-text-bs eds-text-color--grey-700">
            {props.secondaryText}
        </span>
    </div>
);

const privacyValuesMap = PRIVACY_VALUES.map((privacyValue) => ({
    value: privacyValue.value,
    display: (
        <RadioItemWithSecondaryText
            display={privacyValue.display}
            secondaryText={privacyValue.secondaryText}
        />
    ),
}));

const PrivacySettings = ({
    eventPassword = '',
    isDisabled,
    onEventPasswordChange,
    onPrivacyAccessChange,
    onPrivacyListingChange,
    privacyValues = privacyValuesMap,
    privacyAccess = EVENT_PRIVACY_ACCESS_LINK,
    privacyListing = LISTED_PUBLIC,
}: PrivacySettingsProps): JSX.Element | null => (
    <>
        <div className="eds-g-grid privacy-settings">
            <div className="eds-g-cell eds-l-mar-bot-2 eds-g-cell-11-12 eds-g-cell-ln-9-12">
                <div className="eds-text-weight--heavy">
                    {LOCALIZED_TEXT.whoCanSee}
                </div>
            </div>
        </div>
        <div className="eds-g-grid eds-l-md-mar-bot-8">
            <div className="eds-g-cell eds-g-cell-11-12 eds-g-cell-ln-9-12">
                <Radio
                    name="event-privacy-type"
                    values={privacyValues}
                    onChange={onPrivacyListingChange}
                    value={privacyListing}
                    disabledList={
                        isDisabled
                            ? PRIVACY_VALUES.map(({ value }) => value)
                            : null
                    }
                    key="privacy-type"
                />
            </div>
        </div>

        {privacyListing === LISTED_PRIVATE && (
            <>
                <div className="eds-g-grid eds-l-mar-top-8">
                    <div className="eds-g-cell eds-l-mar-bot-4 eds-g-cell-11-12 eds-g-cell-ln-9-12">
                        <div className="eds-text-weight--heavy">
                            {LOCALIZED_TEXT.chooseAudience}
                        </div>
                    </div>
                </div>
                <div className="eds-g-grid">
                    <div
                        className="eds-g-cell eds-g-cell-11-12 eds-g-cell-ln-9-12"
                        data-spec="privacy-settings-event-privacy-access"
                    >
                        <SelectField
                            style={STYLE_STATIC}
                            label={LOCALIZED_TEXT.audience}
                            data-spec="privacy-settings-privacy-listing"
                            data-automation="coyote-privacy-dropdown"
                            values={EVENT_PRIVACY_ACCESS_VALUES}
                            onChange={onPrivacyAccessChange}
                            value={privacyAccess}
                            disabled={isDisabled}
                            name="event-privacy-access-dropdown"
                        />
                    </div>
                    {privacyAccess === EVENT_PRIVACY_ACCESS_PASSWORD && (
                        <div
                            className="eds-g-cell eds-g-cell-11-12 eds-g-cell-ln-9-12 eds-l-mar-top-2"
                            data-spec="privacy-settings-private-event-password"
                        >
                            <InputField
                                id="privacy-settings-event-password"
                                style={STYLE_STATIC}
                                label={PASSWORD_LABEL}
                                hasCharacterCount
                                maxLength={PASSWORD_MAX_LENGTH}
                                value={eventPassword}
                                onChange={onEventPasswordChange}
                                disabled={isDisabled}
                                placeholder={PASSWORD_LABEL}
                            />
                        </div>
                    )}
                </div>
            </>
        )}
    </>
);

export default PrivacySettings;
